import type {
  CoverPhotoSaveData,
  DraftEventSaveOptions,
  EventSaveErrorProps,
  EventSaveGAEventParams,
  EventSaveOptions,
  EventSaveResponse,
  EventWizardOnDraftSaved,
  EventWizardOnEventSaved,
  EventWizardReduxState,
  LatestOrganiserEventResponse,
} from '@Components/event-wizard/event-wizard.types';
import {EVENT_ERROR_LOG_TAG} from '@Components/event-wizard/event-wizard.types';
import {EventCreationErrorType, EventWizardProgressStep, EventWizardSourceType, LOADING_TOAST_KEY, MESSAGE_GROWL_KEY, RegistrationMode} from '@Components/event-wizard/event-wizard.types';
import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {RecurringType} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import type {EventRegistrationFields} from '@Components/events/events.types';
import type {TicketingConfig} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {buildTicketingConfigFromEventVO, getEffectiveQuestionSectionTitle, getEffectiveRegisterBtnText, getEventTypeGa4Param, getTicketingSetupCompleteParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import type {OrganiserDetails} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import type {CoverPhoto} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import type {EventStyles} from '@Components/event-wizard/event-style/event-style.types';
import {redirectUser} from '@Utils/browser.util';
import type {EventWizardVenue} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import {EventVenueType, isOfflineVenueComplete} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import type {EventHashedID, EventVO, EventVOMinifiedWizardPrefill} from '@Components/events/events.types';
import {
  getDefaultEndDateTime,
  getDefaultRecurringEvent,
  getDefaultSelectedOrganiserDetails,
  getDefaultStartDateTime,
  getDefaultTimeZone,
  onEventWizardCoverPhotoUpdate,
  onEventWizardDescriptionChange,
  onEventWizardEndDateTimeUpdate,
  onEventWizardEventHIDChange,
  onEventWizardIsDesignPrefillContentLoadingUpdate,
  onEventWizardIsDraftBeingSavedUpdate,
  onEventWizardIsEventBeingCreatedUpdate,
  onEventWizardOfflineVenueChange,
  onEventWizardOnlineVenueChange,
  onEventWizardPageSourceTypeUpdate,
  onEventWizardProgressStepLandedOnUpdate,
  onEventWizardProgressStepUpdate,
  onEventWizardRecurringChange,
  onEventWizardEventRegistrationChange,
  onEventWizardRegistrationModeChange,
  onEventWizardRemoveWatermarkToggle,
  onEventWizardSelectedOrganiserUpdate,
  onEventWizardSelectedTypeChange,
  onEventWizardShareAsGoogleEventToggle,
  onEventWizardShareInTeamToggle,
  onEventWizardTicketingConfigHydrate,
  onEventWizardStartDateTimeUpdate,
  onEventWizardStyleColorUpdate,
  onEventWizardStyleLegibilityUpdate,
  onEventWizardStyleUpdate,
  onEventWizardTimeZoneUpdate,
  onEventWizardSavedContentSnapshotUpdate,
  onEventWizardTitleChange,
  onEventWizardToggleCountdown,
} from '@Components/event-wizard/event-wizard-slice';
import type {TimeZone} from 'timezones-list';
import timezones from 'timezones-list';
import {addHoursToDate, convertTimeFromTimezoneToUTC, convertUTCTimeToGivenTimezone, getSQLFormatDate, getTimeDifferenceIntervals, hasDatedPassed} from '@Utils/date.util';
import {getDateTimeErrorType} from '@Components/event-wizard/event-wizard-date-time/event-wizard-date-time.helper';
import {DateTimeErrorType} from '@Components/event-wizard/event-wizard-date-time/event-wizard-date-time.types';
import {deriveSelectedRegistrationMode, getPromotePageURL, markEventAsJustCreated, openUpsellDialogForEventsLimitReached} from '@Libraries/events.library';
import {getTicketingDashboardURL, isTicketedEvent, isTicketingAvailableForEvent} from '@Libraries/event-ticketing.library';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import isEqual from 'lodash/isEqual';
import isInteger from 'lodash/isInteger';
import isNaN from 'lodash/isNaN';
import {togglePreviewCoverPhotoLoading, togglePreviewTitleLoading, updatePreviewDateTime, updatePreviewTitle, updateStyleColor} from '@Components/event-wizard/preview/preview.helper';
import type {ColorLegibilityState} from '@Libraries/event-colors.library';
import {getLegibilityMemo} from '@Libraries/event-colors.library';
import {getEventColorScheme} from '@Components/event-wizard/event-style/event-style.helper';
import {StyleVariant} from '@Components/event-wizard/event-style/event-style.types';
import {isBasicSectionComplete} from '@Components/event-wizard/event-wizard-review/event-wizard-review.helper';
import {hideLoading, showLoading, smoothEstimateLoadingProgress, updateLoadingProgress} from '@Libraries/loading-toast-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {GA4EventName, GA4EventParamName, getEmbeddedEventWizardsGA4SourceParam} from '@Libraries/ga-events';
import {validateSocialMediaLinks} from '@Components/social-media-profile-links/social-media-profile-links-library';
import {shouldUpsellAITemplateTagging} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {getGMTOffsetFromTimeZone} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {openUserEmailVerificationModal} from '@Modals/user-email-verification-modal/user-email-verification-modal.types';
import {isValidURL2} from '@Utils/url.util';
import {openUserEmailVerificationErrorModal} from '@Modals/user-email-verification-error-modal/user-email-verification-error-modal.types';
import {pollHasUserVerifiedEmail, sendUserVerificationEmail} from '@Libraries/user.library';
import {openEventDraftSavedModal} from '@Modals/event-draft-saved-modal/event-draft-saved-modal.library';

export const goToOriginPage = (): void => {
  window.history.back();
};

export const removeRedundantQueryParams = (): void => {
  history.replaceState({}, '', window.location.origin + window.location.pathname);
};

/**
 * Checks if an element has a class or is inside a parent class
 * @param element
 * @param classNameToExclude
 */
export const isElementInClass = (element: HTMLElement | null, classNameToExclude: string): boolean => {
  let elementRecur = element;

  while (elementRecur) {
    if (elementRecur.classList.contains(classNameToExclude)) {
      return true;
    }
    elementRecur = elementRecur.parentElement;
  }
  return false;
};

export const setReduxForDesignPrefill = (eventVOMinifiedWizardPrefill?: EventVOMinifiedWizardPrefill): void => {
  const designTemplateSource: MediaTypes | undefined = eventVOMinifiedWizardPrefill?.designTemplateSource;

  if (
    !eventVOMinifiedWizardPrefill?.designTemplateHID ||
    !eventVOMinifiedWizardPrefill.designTemplateURL ||
    designTemplateSource === undefined ||
    !(designTemplateSource in MediaTypes)
  ) {
    return;
  }

  const {dispatch} = window.PMW.redux.store;

  dispatch(
    onEventWizardCoverPhotoUpdate({
      uploadedUrl: eventVOMinifiedWizardPrefill.designTemplateURL,
      selectedTemplateHID: eventVOMinifiedWizardPrefill.designTemplateHID,
      mediaType: eventVOMinifiedWizardPrefill.designTemplateSource,
      eventPresetImageHash: null,
    })
  );
  trackDesignPrefillGAEvent();

  if (eventVOMinifiedWizardPrefill.isContentPrefillSkipped) {
    return;
  }

  prefillContentFromDesign(eventVOMinifiedWizardPrefill);
};

/**
 * Stores the palette for a background/text pairing, stores what the legibility banner needs, and repaints the live
 * preview. The style step and the gallery preset prefill both go through here, so a prefilled background gets the
 * same derived, save-legible text as a picked one.
 */
export const applyEventWizardColorLegibility = (legibilityState: ColorLegibilityState): void => {
  const {dispatch} = window.PMW.redux.store;
  const scheme = getEventColorScheme({background: legibilityState.background, contentColor: legibilityState.contentColor}, StyleVariant.BALANCED);

  dispatch(onEventWizardStyleColorUpdate(scheme));
  dispatch(onEventWizardStyleLegibilityUpdate(getLegibilityMemo(legibilityState)));
  updateStyleColor(scheme);
};

export const setReduxForStartDatePrefill = (eventVOMinifiedWizardPrefill?: EventVOMinifiedWizardPrefill): void => {
  if (!eventVOMinifiedWizardPrefill?.startDateTime) {
    return;
  }
  const {dispatch} = window.PMW.redux.store;
  const timeZone = getDefaultTimeZone();
  const startDateTime = new Date(eventVOMinifiedWizardPrefill.startDateTime);
  const endDateTime = addHoursToDate(startDateTime);
  const endDateTimeUTC = convertTimeFromTimezoneToUTC(endDateTime, getGMTOffsetFromTimeZone(timeZone));

  if (hasDatedPassed(endDateTimeUTC)) {
    return;
  }
  dispatch(onEventWizardStartDateTimeUpdate(startDateTime));
  dispatch(onEventWizardEndDateTimeUpdate(endDateTime));
};

export const setReduxForUpdatingEvent = (eventVO?: EventVO): void => {
  if (!eventVO) {
    return;
  }
  const {dispatch} = window.PMW.redux.store;

  dispatch(onEventWizardEventHIDChange(eventVO.hashedID));
  dispatch(onEventWizardTitleChange(eventVO.title));
  if (eventVO.type) {
    dispatch(onEventWizardSelectedTypeChange(eventVO.type));
  }
  dispatch(onEventWizardDescriptionChange(eventVO.description));
  setReduxCoverPhotoFromUpdateEvent(eventVO);
  setReduxVenueFromUpdateEvent(eventVO);
  setReduxRecurringForUpdateEvent(eventVO.recurring);
  const timezone = setReduxTimezoneFromUpdateEvent(eventVO.tzcode);
  if (eventVO.startDateTime && eventVO.endDateTime) {
    attemptToSetReduxDateTimeFromGivenStartAndEnd(new Date(eventVO.startDateTime), new Date(eventVO.endDateTime), timezone);
  }
  dispatch(onEventWizardToggleCountdown(eventVO.showCountdown));
  dispatch(onEventWizardStyleUpdate(eventVO.style));
  setReduxOrganiserForUpdateEvent(eventVO);
  dispatch(onEventWizardShareInTeamToggle(eventVO.isShared));
  dispatch(onEventWizardRemoveWatermarkToggle(eventVO.isRemoveWatermark));
  dispatch(onEventWizardEventRegistrationChange(eventVO.registrationFields));
  dispatch(onEventWizardTicketingConfigHydrate(buildTicketingConfigFromEventVO(eventVO.ticketingConfig, eventVO.registrationFields.registerBtnText)));
  dispatch(
    onEventWizardRegistrationModeChange(
      deriveSelectedRegistrationMode(eventVO.ticketingConfig?.isSelected ?? false, eventVO.ticketingConfig?.isRsvp ?? false, eventVO.registrationFields.areRegistrationsRequired, eventVO.registrationFields.registerBtnLink)
    )
  );
  dispatch(onEventWizardShareAsGoogleEventToggle(!!eventVO.isSharedOnGoogle));
  dispatch(onEventWizardSelectedOrganiserUpdate(eventVO.organiser ?? getDefaultSelectedOrganiserDetails()));
};

export const doesContainKeywords = (searchText: string, keywords: string): boolean => {
  return searchText.toLowerCase().includes(keywords.toLowerCase());
};

export const saveEvent = async (data: EventWizardReduxState, saveOptions: EventSaveOptions): Promise<void> => {
  return window.PMW.writeLocal('event/saveEvent', prepareDataForEventCreation(data))
    .then((responseData: EventSaveResponse) => {
      onEventSuccessCreation(responseData, data, saveOptions);
    })
    .catch((error: EventSaveErrorProps) => {
      onEventFailedCreation(error, saveOptions);
    });
};

export const saveDraftEvent = async (data: EventWizardReduxState, saveOptions: DraftEventSaveOptions): Promise<void> => {
  return window.PMW.writeLocal('event/saveDraftEvent', prepareDataForEventCreation(data))
    .then((responseData: {eventVO: EventVO}) => {
      onDraftEventSuccessSave(responseData.eventVO, saveOptions.onSaved);
    })
    .catch((error: EventSaveErrorProps) => {
      onEventFailedCreation(error, saveOptions);
    });
};

/**
 * The save that ends the create. Whatever opened this page -- a blank create, a duplicate, a design prefill --
 * the event now has a row of its own, so the session becomes an UPDATE and the URL follows it. That is the
 * state a refresh would land in, so the tab and the reload now behave the same instead of diverging.
 *
 * Deliberate consequence: a drafted-then-published event reports to GA as an update rather than a create and
 * skips the first-event confetti, because both read `pageSourceType`. The reload path already did this; the
 * reset makes it one behaviour instead of two. The create is the draft save, not the publish that follows it.
 */
const onDraftEventSuccessSave = (eventVO: EventVO, onDraftSaved?: EventWizardOnDraftSaved): void => {
  const {dispatch} = window.PMW.redux.store;
  dispatch(onEventWizardEventHIDChange(eventVO.hashedID));
  dispatch(onEventWizardPageSourceTypeUpdate(EventWizardSourceType.UPDATE));
  dispatch(onEventWizardIsDraftBeingSavedUpdate(false));
  dispatch(onEventWizardIsEventBeingCreatedUpdate(false));
  markEventWizardContentAsSaved();
  if (onDraftSaved) {
    onDraftSaved(eventVO);
    return;
  }
  pointTheURLAtTheSavedDraft_(eventVO.hashedID);
  openEventDraftSavedModal({
    onGoToMyEvents: navigateToMyStuffEvents,
  });
};

/**
 * The first draft save creates the event, and until this runs the address bar still reads `event/wizard` --
 * the *create* route. Redux holds the new id, so saving again from the open tab correctly updates the same
 * row; a **refresh** does not, because the reload starts a blank create and the next save writes a second
 * event. An organiser who refreshes to check their work is the ordinary way to reach that.
 *
 * Rewriting the address in place makes a refresh reload the draft that was just saved, and costs no history
 * entry -- the back button still goes wherever they came from, not to an empty wizard.
 */
const pointTheURLAtTheSavedDraft_ = (eventHashedID: EventHashedID): void => {
  window.history.replaceState(null, '', window.PMW.util.site_url(`event/wizard/${eventHashedID}`));
};

/**
 * Same-tab navigation to My Stuff, shared by the publish path and the draft-saved modal's Go to my events.
 *
 * Two things this depends on, both of which have already bitten:
 *
 * 1. **The caller must not close the modal first.** `onClose()` unmounts the panel, and a navigation started
 *    in the same handler can die with it -- the request shows in the network tab and the page never changes.
 *    The draft-saved modal therefore stays open with a spinner until the page actually changes (T2.17).
 * 2. `markEventWizardContentAsSaved()` ran when the draft saved, so the `beforeunload` guard is already
 *    released and this navigation is not second-guessed by the unsaved-changes prompt.
 */
const navigateToMyStuffEvents = (): void => {
  redirectUser(window.PMW.util.site_url(`posters/mine${MY_STUFF_HASHES.events}`), false, false);
};

export const getEventCreationTimeFormat = (dateTime: Date): string => {
  return dateTime.toLocaleString('en-us');
};

/**
 * Where the wizard drops the organiser after a save — the tab they will actually work in next, rather than the
 * public event page. Three destinations, in priority order.
 *
 * An organiser who still has to set up payments goes to the Tickets page, on CREATE and UPDATE alike, because
 * the onboarding view lives there and it is the same next job either way. **The backend owns the meaning of
 * that flag** — it is `isPaidTicketing() && requiresOnboardingTakeover()`, so a RESTRICTED organiser gets
 * `false` on purpose and lands on Promote where the banner explains the restriction. Do NOT branch on the
 * account state here: re-deriving the rule would need a second round trip purely to decide where to
 * navigate, and would re-open the two-copies-of-one-rule seam this closes.
 *
 * Otherwise, editing an existing ticketed event lands on its Tickets dashboard, where the orders are. A newly
 * created or duplicated event has no orders yet, so it lands on Promote whatever its registration mode is:
 * filling the seats is the organiser's next job. RSVP, custom-link and no-button events have nothing to
 * manage on the dashboard.
 *
 * `isTicketingAvailableForEvent` is not a re-derivation of the backend rule: it answers a different question
 * — may this VIEWER open the Tickets tab at all? Without it a viewer whose gating hides the tab would be
 * deep-linked to a page `event-ticketing.tsx`'s own redirect immediately bounces to #/my-events, which reads
 * as a broken save.
 */
const getPostSaveRedirectURL = (event: EventVO, pageSourceType: EventWizardSourceType, needsStripeOnboarding: boolean): string => {
  if (needsStripeOnboarding && isTicketingAvailableForEvent(event)) {
    return getTicketingDashboardURL(event);
  }
  if (pageSourceType === EventWizardSourceType.UPDATE && isTicketedEvent(event) && isTicketingAvailableForEvent(event)) {
    return getTicketingDashboardURL(event);
  }
  return getPromotePageURL(event);
};

const onEventSuccessCreation = (
  {eventVO: event, wasTicketingPaidJustEnabled, needsStripeOnboarding}: EventSaveResponse,
  data: EventWizardReduxState,
  {pageSourceType, onSaved, ga4Source}: EventSaveOptions
): void => {
  try {
    handleEventSuccessCreationGAEvent({pageSourceType, data, wasTicketingPaidJustEnabled, ga4Source});
  } catch (e) {
    console.error(e);
  }
  if (pageSourceType !== EventWizardSourceType.UPDATE) {
    markEventAsJustCreated();
  }
  markEventWizardContentAsSaved();
  if (onSaved) {
    onSaved(event);
    return;
  }
  redirectUser(getPostSaveRedirectURL(event, pageSourceType, needsStripeOnboarding), false, false);
};

const handleEventSuccessCreationGAEvent = ({pageSourceType, data, wasTicketingPaidJustEnabled, ga4Source}: EventSaveGAEventParams): void => {
  const sourceParam = getEmbeddedEventWizardsGA4SourceParam(ga4Source);
  if (pageSourceType === EventWizardSourceType.UPDATE) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPDATE_CLICK, sourceParam);
    if (wasTicketingPaidJustEnabled) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_TICKETING_PAID_ENABLED);
    }
    return;
  }
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_COMPLETE, {
    [GA4EventParamName.EVENT_TYPE]: getEventTypeGa4Param(data.selectedRegistrationMode, data.ticketingConfig),
    ...sourceParam,
  });
  if (data.selectedRegistrationMode === RegistrationMode.TICKETS) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_TICKETING_SETUP_COMPLETE, getTicketingSetupCompleteParams(data.ticketingConfig));
  }
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_RECURRING, {[GA4EventParamName.TYPE]: data.recurringEvent.isRecurring ? 'Recurring' : 'Non-recurring'});
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VENUE, {
    [GA4EventParamName.TYPE]: data.venue.selectedVenueType === EventVenueType.OFFLINE ? 'Offline' : 'Online',
  });

  if (data.coverPhoto.mediaType === MediaTypes.UPLOAD) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VISUALS, {[GA4EventParamName.TYPE]: 'Upload'});
  } else if (data.coverPhoto.mediaType === MediaTypes.MY_DESIGNS) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VISUALS, {[GA4EventParamName.TYPE]: 'My Designs'});
  } else if (data.coverPhoto.mediaType === MediaTypes.TEMPLATES) {
    if (shouldUpsellAITemplateTagging(data.doesPAYGUserHaveAITemplateLimit)) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VISUALS, {[GA4EventParamName.TYPE]: 'Gallery non AI'});
    } else {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VISUALS, {[GA4EventParamName.TYPE]: 'AI'});
    }
  }

  if (data.isSharedInTeam) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_SHARE_TEAM);
  }
  const validatedSocialMediaLinks = validateSocialMediaLinks(data.organisers.selectedOrganiser.socialMediaLinks);

  if (validatedSocialMediaLinks.isValid) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_PAGE_SOCIALS);
  }
};

const onEventFailedCreation = (error: EventSaveErrorProps, {onFail, onSaved}: EventSaveOptions | DraftEventSaveOptions): void => {
  const errMsg = error.message;

  if (errMsg === EventCreationErrorType.NUM_CREATION_LIMIT.toString()) {
    openUpsellDialogForEventsLimitReached();
    onFail(false);
    return;
  }

  if (errMsg === EventCreationErrorType.RACE_CONDITION.toString()) {
    onFail(true);
    return;
  }

  if (errMsg === EventCreationErrorType.RECURRING_PAID_TICKETING_NOT_SUPPORTED.toString()) {
    showMessageGrowl({
      key: MESSAGE_GROWL_KEY,
      text: window.i18next.t('pmwjs_recurring_paid_ticketing_save_failed'),
      interval: 15000,
      type: GROWL_TYPE.DANGER,
      showCloseIcon: true,
    });
    onFail(false);
    return;
  }

  if (errMsg === EventCreationErrorType.TICKETING_COUNTRY_NOT_SUPPORTED.toString()) {
    showMessageGrowl({
      key: MESSAGE_GROWL_KEY,
      text: window.i18next.t('pmwjs_ticketing_country_not_supported'),
      interval: 15000,
      type: GROWL_TYPE.DANGER,
      showCloseIcon: true,
    });
    onFail(false);
    return;
  }

  if (errMsg === EventCreationErrorType.USER_EMAIL_VERIFICATION_REQUIRED.toString()) {
    if (error.data.errorMessage) {
      onFail(false);
      openUserEmailVerificationErrorModal({errorMsg: error.data.errorMessage});
      return;
    }
    openUserEmailVerificationModal({
      userEmail: error.data.userEmail,
      sniperLink: error.data.sniperLink,
      onClose: () => {
        onFail(false);
      },
      onVerifiedCB: () => {
        void onEmailVerifiedAfterSaveAttempt(onSaved);
      },
      pollVerification: pollHasUserVerifiedEmail,
      sendVerification: () => sendUserVerificationEmail({is_event: 1}),
    });
    return;
  }
  onFail(true);
};

/**
 * The verification link saves the event parked by the failed save (`Authenticate::publishEventWaitingOnEmailVerification_`),
 * so the wizard must not save it again: a second save would create a second event. The page goes to that event; with a
 * save callback (the embed) the callback gets it instead, falling back to the page's redirect if it cannot be
 * fetched or is not there yet.
 */
const onEmailVerifiedAfterSaveAttempt = async (onSaved?: EventWizardOnEventSaved | EventWizardOnDraftSaved): Promise<void> => {
  if (!onSaved) {
    redirectToEventSavedByEmailVerification();
    return;
  }

  try {
    const {eventVO} = (await window.PMW.readLocal('event/getLatestOrganiserEventAfterEmailVerification')) as LatestOrganiserEventResponse;
    if (eventVO) {
      onSaved(eventVO);
      return;
    }
  } catch (error) {
    console.error(error);
  }
  redirectToEventSavedByEmailVerification();
};

const redirectToEventSavedByEmailVerification = (): void => {
  redirectUser(window.PMW.util.site_url('event/redirectToLatestOrganiserEventAfterEmailVerification'), false, false);
};

const setReduxCoverPhotoFromUpdateEvent = (eventVO: EventVO): void => {
  let url = eventVO.img,
    mediaType = undefined,
    uploadMediaHID = undefined;

  if (eventVO.coverPhotoTemplateHID && eventVO.coverPhotoPreviewURL) {
    mediaType = MediaTypes.MY_DESIGNS;
    url = eventVO.coverPhotoPreviewURL;
  } else if (url) {
    mediaType = MediaTypes.UPLOAD;
    const filenameWithExt = url.substring(url.lastIndexOf('/') + 1);
    uploadMediaHID = filenameWithExt.substring(0, filenameWithExt.lastIndexOf('.'));
  }
  window.PMW.redux.store.dispatch(
    onEventWizardCoverPhotoUpdate({mediaType: mediaType, uploadedUrl: url, selectedTemplateHID: eventVO.coverPhotoTemplateHID, uploadMediaHID: uploadMediaHID, eventPresetImageHash: null})
  );
};

const setReduxVenueFromUpdateEvent = (eventVO: EventVO): void => {
  const {dispatch} = window.PMW.redux.store;

  if (eventVO.venueOnline) {
    dispatch(onEventWizardOnlineVenueChange(eventVO.venueOnline.link));
    return;
  }

  if (eventVO.venue && isOfflineVenueComplete(eventVO.venue)) {
    dispatch(onEventWizardOfflineVenueChange(eventVO.venue));
  }
};

const setReduxTimezoneFromUpdateEvent = (tzcode: string): TimeZone => {
  for (const timezone of timezones) {
    if (timezone.tzCode === tzcode) {
      window.PMW.redux.store.dispatch(onEventWizardTimeZoneUpdate(timezone));
      return timezone;
    }
  }
  return getDefaultTimeZone();
};

const setReduxRecurringForUpdateEvent = (recurring: RecurringEvent | undefined): void => {
  if (!recurring?.recurringEndDateStr) {
    return;
  }
  recurring.recurringEndDate = new Date(recurring.recurringEndDateStr);

  const defaultRecurring = getDefaultRecurringEvent();

  if (recurring.recurringType === RecurringType.WEEKLY) {
    recurring.recurringMonthly = defaultRecurring.recurringMonthly;
  } else if (recurring.recurringType === RecurringType.MONTHLY) {
    recurring.recurringWeeks = defaultRecurring.recurringWeeks;
  } else {
    recurring.recurringWeeks = defaultRecurring.recurringWeeks;
    recurring.recurringMonthly = defaultRecurring.recurringMonthly;
  }
  window.PMW.redux.store.dispatch(onEventWizardRecurringChange({...recurring, isRecurring: true}));
};

const attemptToSetReduxDateTimeFromGivenStartAndEnd = (startDateTimeUTC: Date, endDateTimeUTC: Date, timeZone: TimeZone): Date[] => {
  const {dispatch} = window.PMW.redux.store;

  const errType = getDateTimeErrorType(startDateTimeUTC, endDateTimeUTC, timeZone, true);
  let startDateTimeUpdated: Date = startDateTimeUTC;
  let endDateTimeUpdated: Date = endDateTimeUTC;

  if (errType && errType !== DateTimeErrorType.EVENT_PASSED_DATE && errType !== DateTimeErrorType.EVENT_PASSED_TIME) {
    return [];
  }

  if (errType) {
    // If the event has passed, it goes to the next day from the current day WHILE keeping the hour/minute data the same
    const {intervalInDays} = getTimeDifferenceIntervals((endDateTimeUTC.getTime() - startDateTimeUTC.getTime()) / 1000);
    const currentTimeInUTC = convertTimeFromTimezoneToUTC(new Date(), getGMTOffsetFromTimeZone(timeZone));
    startDateTimeUpdated = new Date(startDateTimeUTC.getTime());
    startDateTimeUpdated.setDate(currentTimeInUTC.getDate() + 1);
    endDateTimeUpdated = new Date(endDateTimeUTC.getTime());
    endDateTimeUpdated.setFullYear(startDateTimeUpdated.getFullYear(), startDateTimeUpdated.getMonth(), startDateTimeUpdated.getDate() + intervalInDays);
  }

  const userInputStartDateTime = convertUTCTimeToGivenTimezone(startDateTimeUpdated, getGMTOffsetFromTimeZone(timeZone));
  const userInputEndDateTime = convertUTCTimeToGivenTimezone(endDateTimeUpdated, getGMTOffsetFromTimeZone(timeZone));
  dispatch(onEventWizardStartDateTimeUpdate(userInputStartDateTime));
  dispatch(onEventWizardEndDateTimeUpdate(userInputEndDateTime));

  return [userInputStartDateTime, userInputEndDateTime];
};

const setReduxOrganiserForUpdateEvent = (eventVO: EventVO): void => {
  if (!eventVO.organiser) {
    return;
  }
  const {dispatch} = window.PMW.redux.store;
  dispatch(onEventWizardSelectedOrganiserUpdate({...eventVO.organiser, isProfilePicturePrefilled: !!eventVO.organiser.profilePic}));
};

/**
 * "Is there anything to save?" answered by the payload itself rather than by a dirty flag on 71 reducers.
 *
 * This is the exact body a save would POST, so it cannot drift from what the wizard persists, and a field added
 * later is covered with no extra wiring. It ignores everything `prepareDataForEventCreation` ignores -- open
 * panels, the current step, in-flight flags -- because none of that is content.
 */
export type EventWizardSavedContent = ReturnType<typeof prepareDataForEventCreation>;

export const getEventWizardContent = (data: EventWizardReduxState): EventWizardSavedContent => {
  return prepareDataForEventCreation(data);
};

export const hasEventWizardUnsavedContent = (data: EventWizardReduxState): boolean => {
  if (data.savedContentSnapshot === null) {
    return false;
  }

  return !isEqual(getEventWizardContent(data), data.savedContentSnapshot);
};

/**
 * Marks the wizard's current content as the saved baseline. Called once the wizard has settled (so a freshly
 * opened draft is not "unsaved"), and again after every successful save.
 */
export const markEventWizardContentAsSaved = (): void => {
  const {dispatch} = window.PMW.redux.store;
  dispatch(onEventWizardSavedContentSnapshotUpdate(getEventWizardContent(window.PMW.redux.store.getState().eventWizard)));
};

const prepareDataForEventCreation = (data: EventWizardReduxState) => {
  const venueData = getVenueData(data.venue);
  const registrationFields = getRegistrationFieldsData(
    data.registrationDetails,
    getEffectiveRegisterBtnText(data.selectedRegistrationMode, data.ticketingConfig, data.registrationDetails)
  );
  const organiserData = getOrganisationData(data.organisers.selectedOrganiser);
  const coverPhotoData = getCoverPhotoData(data.coverPhoto);
  const styles = getStyleData(data.styles);

  return {
    event_hid: data.eventHID,
    title: data.title,
    event_type_hid: data.types?.selectedType?.hashedID,
    description: data.description,
    ...coverPhotoData,
    ...venueData,
    date_start: getEventCreationTimeFormat(data.startDateTime),
    date_end: getEventCreationTimeFormat(data.endDateTime),
    recurring_pattern: getRecurringPatternForPost(data.recurringEvent),
    timezone: data.timeZone.tzCode,
    show_countdown: data.showCountdown ? 1 : 0,
    ...styles,
    ...organiserData,
    is_shared_team: data.isSharedInTeam ? 1 : 0,
    is_remove_watermark: data.isRemoveWatermark ? 1 : 0,
    is_shared_google: data.isSharedAsGoogleEvent ? 1 : 0,
    ...registrationFields,
    ...getTicketingConfigData(data.ticketingConfig),
    is_duplicate: data.pageSourceType === EventWizardSourceType.DUPLICATE ? 1 : 0,
    is_description_user_made: data.isDescriptionUserMade ? 1 : 0,
  };
};

const getVenueData = (venue: EventWizardVenue) => {
  if (venue.selectedVenueType === EventVenueType.OFFLINE) {
    return getOfflineVenueData(venue.offlineVenue);
  }

  if (venue.selectedVenueType === EventVenueType.ONLINE) {
    return getOnlineVenueData(venue.onlineVenueURL);
  }
  return null;
};

const getOnlineVenueData = (url: string | undefined) => {
  if (!url) {
    return null;
  }

  return {
    online_event_link: url,
  };
};

const getOfflineVenueData = (place: PlaceDetails | undefined) => {
  if (!place) {
    return null;
  }

  return {
    offline_venue: {
      venue_id: place.idAddress,
      venue_new_place_id: place.placeID,
      name: place.name,
      address: place.address,
      address_directions: place.addressDirections,
      lng: place.longitude,
      lat: place.latitude,
      country: place.country,
      province: place.province,
      city: place.city,
      postal_code: place.postalCode,
    },
  };
};

const getRegistrationFieldsData = (registrationFields: EventRegistrationFields, registerBtnText: string) => {
  return {
    registration_fields: {
      are_registrations_required: registrationFields.areRegistrationsRequired ? 1 : 0,
      name: registrationFields.name ? 1 : 0,
      phone: registrationFields.phone ? 1 : 0,
      register_btn_text: registerBtnText,
      register_btn_link: isValidURL2(registrationFields.registerBtnLink ?? '') ? registrationFields.registerBtnLink : undefined,
    },
  };
};

const getTicketingConfigData = (config: TicketingConfig): Record<string, unknown> => {
  if (!config.isSelected) {
    return {}; // omit ticketing_config entirely — backend validates unconditionally otherwise
  }
  return {
    ticketing_config: {
      is_selected: 1,
      is_rsvp: config.isRsvp ? 1 : 0,
      ticket_name: config.ticketName,
      price: config.price,
      total_tickets: config.totalTickets,
      max_per_order: config.maxPerOrder,
      pass_fees_to_buyer: config.passFeesToBuyer && config.price > 0 ? 1 : 0,
      tax_label: config.taxLabel,
      tax_rate: config.taxRatePercent,
      tax_mode: config.taxMode,
      who_responds: config.whoResponds,
      question_section_title: getEffectiveQuestionSectionTitle(config),
      question_section_description: config.questionSectionDescription,
      questions: config.questions.map((q, index) => ({
        text: q.text,
        type: q.type,
        required: q.required ? 1 : 0,
        order: index,
        options: q.options,
      })),
    },
  };
};

const getOrganisationData = (organiserDetails: OrganiserDetails) => {
  return {
    organiser_details: {
      hid: organiserDetails.hashedId,
      name: organiserDetails.name,
      email: organiserDetails.email,
      phone: organiserDetails.phone,
      pic: organiserDetails.profilePic,
      isPicPrefilled: organiserDetails.isProfilePicturePrefilled ? 1 : 0,
      socialMediaLinks: organiserDetails.socialMediaLinks,
    },
  };
};

const getRecurringPatternForPost = (recurringEvent: RecurringEvent) => {
  if (!recurringEvent.isRecurring) {
    return null;
  }
  return {
    recurring_type: recurringEvent.recurringType.toString(),
    recurring_end_date: getEventCreationTimeFormat(recurringEvent.recurringEndDate),
    recurring_weeks: recurringEvent.recurringWeeks ? recurringEvent.recurringWeeks.join(',') : null,
    recurring_month_weekday: recurringEvent.recurringMonthly?.weekday,
    recurring_month_weekday_num: recurringEvent.recurringMonthly?.weekdayNum,
  };
};

const getCoverPhotoData = (coverPhoto: CoverPhoto): CoverPhotoSaveData => {
  // The server copies the preset image into the user's uploads on save; the wizard only knows which image it is.
  if (coverPhoto.eventPresetImageHash !== null) {
    return {
      event_preset_image_hash: coverPhoto.eventPresetImageHash,
    };
  }

  if (coverPhoto.selectedTemplateHID) {
    return {
      poster_hid: coverPhoto.selectedTemplateHID,
    };
  }
  return {
    uploaded_media_hid: coverPhoto.uploadMediaHID,
  };
};

const getStyleData = (eventStyles: EventStyles) => {
  return {
    style: {
      colors: {
        primary: eventStyles.colors.primary,
        light: eventStyles.colors.light,
        lighter: eventStyles.colors.lighter,
        dark: eventStyles.colors.dark,
        darker: eventStyles.colors.darker,
        background: eventStyles.colors.background ?? null,
        buttonTextColor: eventStyles.colors.buttonTextColor ?? null,
      },
      font: eventStyles.fontFamily,
    },
  };
};

const prefillContentFromDesign = (eventVOMinifiedWizardPrefill: EventVOMinifiedWizardPrefill): void => {
  window.PMW.redux.store.dispatch(onEventWizardIsDesignPrefillContentLoadingUpdate(true));
  togglePreviewCoverPhotoLoading(true);
  togglePreviewTitleLoading(true);

  showLoading(LOADING_TOAST_KEY, {
    key: LOADING_TOAST_KEY,
    priority: 1,
    text: window.i18next.t('pmwjs_updating_details_with_ai'),
    hideIcon: true,
    progress: 0,
  });
  const estimateLoadingTimeMs = 5000;
  smoothEstimateLoadingProgress(LOADING_TOAST_KEY, estimateLoadingTimeMs, 500);

  const {dispatch} = window.PMW.redux.store;

  void window.PMW.writeLocal('event/getDesignReverseSemanticTaggingContent', {
    poster_hid: eventVOMinifiedWizardPrefill.designTemplateHID,
  })
    .then(setDesignContentFromReverseSemanticTagging)
    .catch((e: Error) => {
      hideLoading(LOADING_TOAST_KEY);

      if (e.message === 'limit_reached' || e.message === 'limit_reached_payg') {
        showMessageGrowl({
          key: MESSAGE_GROWL_KEY,
          text: e.message === 'limit_reached' ? window.i18next.t('pmwjs_daily_ai_generation_limit_reached') : window.i18next.t('pmwjs_ai_generation_limit_reached_payg'),
          interval: 15000,
          type: GROWL_TYPE.DANGER,
          showCloseIcon: true,
        });
      }
    })
    .finally(() => {
      dispatch(onEventWizardIsDesignPrefillContentLoadingUpdate(false));
      togglePreviewCoverPhotoLoading(false);
      togglePreviewTitleLoading(false);
    });
};

const setDesignContentFromReverseSemanticTagging = (eventVOMinifiedWizardPrefill: EventVOMinifiedWizardPrefill): void => {
  const {dispatch} = window.PMW.redux.store;

  if (eventVOMinifiedWizardPrefill.eventName) {
    dispatch(onEventWizardTitleChange(eventVOMinifiedWizardPrefill.eventName));
    updatePreviewTitle(eventVOMinifiedWizardPrefill.eventName);
  }

  if (eventVOMinifiedWizardPrefill.eventType) {
    dispatch(onEventWizardSelectedTypeChange(eventVOMinifiedWizardPrefill.eventType));
  }

  try {
    const startAndEndDateTime = updateDateTimeFromDesignPrefill(eventVOMinifiedWizardPrefill);

    if (startAndEndDateTime.length === 2) {
      eventVOMinifiedWizardPrefill.startDateTime = getSQLFormatDate(startAndEndDateTime[0]);
      eventVOMinifiedWizardPrefill.endDateTime = getSQLFormatDate(startAndEndDateTime[1]);
    }
  } catch (error: unknown) {
    const errMsg = error instanceof Error ? error.message : '';
    window.PMW.log(errMsg + '. ' + EVENT_ERROR_LOG_TAG);
  }

  // selfCodeReviewJibran: Location needs handling. Hit gmap API and set. Attempt timezone too: // location?: string;

  if (isBasicSectionCompleteFromMinifiedVO(eventVOMinifiedWizardPrefill)) {
    dispatch(onEventWizardProgressStepUpdate(EventWizardProgressStep.REVIEW));
    dispatch(onEventWizardProgressStepLandedOnUpdate(EventWizardProgressStep.REVIEW));
  }
  updateLoadingProgress(LOADING_TOAST_KEY, 1.0);
  hideLoading(LOADING_TOAST_KEY);
};

const isBasicSectionCompleteFromMinifiedVO = (eventVOMinifiedWizardPrefill: EventVOMinifiedWizardPrefill): boolean => {
  if (
    !eventVOMinifiedWizardPrefill.eventName ||
    !eventVOMinifiedWizardPrefill.eventType ||
    !eventVOMinifiedWizardPrefill.startDateTime ||
    !eventVOMinifiedWizardPrefill.endDateTime
  ) {
    return false;
  }

  return isBasicSectionComplete(
    eventVOMinifiedWizardPrefill.eventName,
    {}, // selfCodeReviewJibran: when gmap api ready. add in if statement above too
    new Date(eventVOMinifiedWizardPrefill.startDateTime),
    new Date(eventVOMinifiedWizardPrefill.endDateTime),
    getDefaultTimeZone()
  );
};

const updateDateTimeFromDesignPrefill = (eventVOMinifiedWizardPrefill: EventVOMinifiedWizardPrefill): Date[] => {
  if (eventVOMinifiedWizardPrefill.startDate && !eventVOMinifiedWizardPrefill.endDate) {
    eventVOMinifiedWizardPrefill.endDate = eventVOMinifiedWizardPrefill.startDate;
  }

  const startDateTime = getUpdateDateTimeFromDesignPrefill(getDefaultStartDateTime(), eventVOMinifiedWizardPrefill.startDate, eventVOMinifiedWizardPrefill.startTime);
  let endDateTime = getUpdateDateTimeFromDesignPrefill(getDefaultEndDateTime(), eventVOMinifiedWizardPrefill.endDate, eventVOMinifiedWizardPrefill.endTime);
  const timeZone = getDefaultTimeZone();

  if (eventVOMinifiedWizardPrefill.startTime && !eventVOMinifiedWizardPrefill.endTime) {
    endDateTime = addHoursToDate(startDateTime);
  }
  const startAndEndDateTime = attemptToSetReduxDateTimeFromGivenStartAndEnd(
    convertTimeFromTimezoneToUTC(startDateTime, getGMTOffsetFromTimeZone(timeZone)),
    convertTimeFromTimezoneToUTC(endDateTime, getGMTOffsetFromTimeZone(timeZone)),
    timeZone
  );

  if (startAndEndDateTime.length === 2) {
    updatePreviewDateTime({
      startDateTime: startAndEndDateTime[0],
      endDateTime: startAndEndDateTime[1],
      timeZone: timeZone,
    });
  }
  return startAndEndDateTime;
};

const getUpdateDateTimeFromDesignPrefill = (defaultDateTime: Date, updatedDate: string | undefined, updatedTime: string | undefined): Date => {
  if (!updatedTime && !updatedDate) {
    return defaultDateTime;
  }

  if (updatedDate) {
    const newStartDate = new Date(updatedDate);

    if (!isNaN(newStartDate.getTime())) {
      defaultDateTime.setFullYear(defaultDateTime.getFullYear(), newStartDate.getMonth(), newStartDate.getDate());
    }
  }

  if (updatedTime) {
    const [hours, minutes, _] = updatedTime.split(':').map(Number);

    if (isInteger(hours) && isInteger(minutes)) {
      defaultDateTime.setHours(hours, minutes, 0, 0);
    }
  }
  return defaultDateTime;
};

const trackDesignPrefillGAEvent = (): void => {
  const referrer = document.referrer;
  let url = null;

  try {
    url = new URL(referrer);
  } catch (e) {
    return;
  }

  if (url === null) {
    return;
  }
  const segments = url.pathname
    .split('/')
    .filter(Boolean)
    .filter((segment) => segment !== 'pmw' && segment !== 'index.php');
  const firstSegment = segments[0];
  const firstTwoSegments = segments.slice(0, 2).join('/');

  let createClickSrc = '';

  if (firstTwoSegments === 'art/template') {
    createClickSrc = 'Art/template page';
  } else if (firstTwoSegments === 'poster/view') {
    createClickSrc = 'Poster/view page';
  } else if (firstSegment === 'posterbuilder') {
    createClickSrc = 'Design editor';
  }

  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_CLICK, createClickSrc ? {[GA4EventParamName.SOURCE]: createClickSrc} : undefined);
};
